<script setup>
import { computed, h } from 'vue';
import format from 'date-fns/format';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import UserActivityCell from './UserActivityCell.vue';
import { useReportLoading } from '../../composables/useReportLoading';

const props = defineProps({
  userSummary: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  pageIndex: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();
const userSummary = computed(() => props.userSummary);
const isLoading = computed(() => props.isLoading);
const pageIndex = computed(() => props.pageIndex);
const visibleLoading = useReportLoading(isLoading);

const formatDuration = minutes => {
  if (!minutes || minutes === 0) return '0m';

  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  return `${hours}h ${remainingMinutes}m`;
};

const getPauseCountBadgeClass = count => {
  if (count === 0) {
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  }
  if (count <= 5) {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  }
  if (count <= 10) {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
  }
  return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
};

const totalCount = computed(() => userSummary.value.length);

const tableData = computed(() => {
  return userSummary.value.map(user => ({
    user_id: user.user_id,
    user_name: user.user_name,
    availability_status: user.availability_status,
    active_at: user.active_at,
    last_activity_at: user.last_activity_at,
    last_activity_type: user.last_activity_type,
    last_activity_from_status: user.last_activity_from_status,
    last_activity_to_status: user.last_activity_to_status,
    total_pause_sessions: user.total_pause_sessions,
    average_pause_duration_minutes: user.average_pause_duration_minutes,
    total_pause_duration_minutes: user.total_pause_duration_minutes,
    total_logins: user.total_logins,
    total_logouts: user.total_logouts,
    total_work_hours: user.total_work_hours,
    last_login_at: user.last_login_at,
    last_logout_at: user.last_logout_at,
  }));
});

const pauseCountRender = cellProps => {
  const count = cellProps.getValue();
  return h(
    'span',
    {
      class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPauseCountBadgeClass(count)}`,
    },
    count || 0
  );
};

const durationRender = cellProps => {
  const minutes = cellProps.getValue();
  return h(
    'span',
    {
      class: 'text-slate-800 dark:text-slate-200',
    },
    formatDuration(minutes)
  );
};

const statusClasses = {
  online: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  busy: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  pause: 'bg-woot-100 text-woot-800 dark:bg-woot-900 dark:text-woot-200',
  offline: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
};

const statusRender = cellProps => {
  const status = cellProps.getValue() || 'offline';
  return h(
    'span',
    {
      class: `inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClasses[status]}`,
    },
    t(`USER_ACTIVITY_REPORTS.STATUS.${status.toUpperCase()}`)
  );
};

const activityTypeRender = cellProps => {
  const labels = {
    login: 'Login',
    logout: 'Logout',
    pause: 'Pausa',
    availability_change: 'Mudança de disponibilidade',
    work_session: 'Sessão de trabalho',
    message_sent: 'Mensagem enviada',
    conversation_updated: 'Conversa atualizada',
    contact_updated: 'Contato atualizado',
  };
  const type = cellProps.getValue();
  return type ? labels[type] || type.replaceAll('_', ' ') : '—';
};

const dateTimeRender = cellProps => {
  const value = cellProps.getValue();
  return value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : '—';
};

const lastActivityRender = cellProps => {
  const row = cellProps.row.original;
  const timestamp = dateTimeRender({ getValue: () => row.last_activity_at });
  const type = activityTypeRender({ getValue: () => row.last_activity_type });
  const transition =
    row.last_activity_from_status && row.last_activity_to_status
      ? `${row.last_activity_from_status} → ${row.last_activity_to_status}`
      : null;
  return h('span', { class: 'flex flex-col whitespace-nowrap' }, [
    h('strong', { class: 'font-medium' }, transition || type),
    h('small', { class: 'text-slate-500 dark:text-slate-400' }, timestamp),
  ]);
};

const columnHelper = createColumnHelper();
const columns = computed(() => [
  columnHelper.accessor('user_name', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.AGENT_NAME'),
    cell: cellProps => h(UserActivityCell, cellProps),
    size: 250,
  }),
  columnHelper.accessor('last_activity_at', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.LAST_ACTIVITY'),
    cell: lastActivityRender,
    size: 280,
  }),
  columnHelper.accessor('total_work_hours', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.WORK_HOURS'),
    cell: cellProps => formatDuration(cellProps.getValue() * 60),
    size: 170,
  }),
  columnHelper.accessor('availability_status', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.CURRENT_STATUS'),
    cell: statusRender,
    size: 160,
  }),
  columnHelper.accessor('total_logins', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.LOGINS'),
    size: 90,
  }),
  columnHelper.accessor('total_logouts', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.LOGOUTS'),
    size: 90,
  }),
  columnHelper.accessor('last_login_at', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.LAST_LOGIN'),
    cell: dateTimeRender,
    size: 180,
  }),
  columnHelper.accessor('last_logout_at', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.LAST_LOGOUT'),
    cell: dateTimeRender,
    size: 180,
  }),
  columnHelper.accessor('active_at', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.LAST_HEARTBEAT'),
    cell: dateTimeRender,
    size: 190,
  }),
  columnHelper.accessor('total_pause_sessions', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.PAUSE_COUNT'),
    cell: pauseCountRender,
    size: 150,
  }),
  columnHelper.accessor('average_pause_duration_minutes', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.AVG_PAUSE_DURATION'),
    cell: durationRender,
    size: 180,
  }),
  columnHelper.accessor('total_pause_duration_minutes', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.TOTAL_PAUSE_DURATION'),
    cell: durationRender,
    size: 180,
  }),
]);

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex.value,
    pageSize: 25,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: true,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return totalCount.value;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <div class="user-activity-table-container">
    <div class="user-activity-table-scroll">
      <Table
        :table="table"
        class="max-h-[calc(100vh-21.875rem)] border border-slate-50 dark:border-slate-800"
      />
    </div>
    <Pagination class="mt-2" :table="table" />
    <div v-if="visibleLoading" class="user-activity-loader">
      <Spinner />
      <span>{{ $t('USER_ACTIVITY_REPORTS.LOADING_MESSAGE') }}</span>
    </div>
    <EmptyState
      v-else-if="!visibleLoading && !userSummary.length"
      :title="$t('USER_ACTIVITY_REPORTS.NO_DATA')"
    />
  </div>
</template>

<style lang="scss" scoped>
.user-activity-table-container {
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  flex: 1;

  .user-activity-table-scroll {
    min-width: max-content;
  }

  &::v-deep table {
    width: max-content;
    min-width: 100%;
  }

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        white-space: nowrap;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
        white-space: nowrap;
      }
    }
  }

  &::v-deep .ve-pagination {
    background-color: transparent;
  }

  &::v-deep .ve-pagination-select {
    display: none;
  }

  .row-user-block {
    display: flex;
    align-items: center;
    text-align: left;

    .user-block {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-width: 0;
      margin: 0 8px;

      .title {
        font-size: 14px;
        margin: 0;
        line-height: 1.2;
        color: var(--s-800);
      }

      .sub-title {
        font-size: 12px;
        color: var(--s-600);
      }
    }
  }

  .table-pagination {
    margin-top: 16px;
    text-align: right;
  }
}

.user-activity-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 32px;
}
</style>
